/*
 * iVAPE Select — theme.js
 * Vanilla ports of the delivered Next.js components:
 *   reveal.tsx, parallax.tsx, magnetic.tsx, tilt.tsx, scroll-progress.tsx,
 *   age-gate.tsx, header.tsx (mobile menu), trust-badges.tsx, home-product-tabs.tsx,
 *   product-filters.tsx, guide-client.tsx, add-to-cart-button.tsx, cart page.
 * Every motion is skipped under prefers-reduced-motion, as in the source.
 */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var body = document.body;
  var flag = function (name) {
    return body.getAttribute('data-motion-' + name) !== 'false';
  };
  var once = function (el, key) {
    if (el.dataset[key]) return false;
    el.dataset[key] = '1';
    return true;
  };

  /* ---------------------------------------------------------------- Reveal */
  var revealObserver = null;
  function initReveal(root) {
    if (reducedMotion || !flag('reveal') || typeof IntersectionObserver === 'undefined') return;
    if (body.classList.contains('shopify-design-mode')) return;
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              revealObserver.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
      );
    }
    root.querySelectorAll('[data-reveal]').forEach(function (el) {
      if (!once(el, 'revealBound')) return;
      el.classList.add('reveal-pending');
      revealObserver.observe(el);
    });
  }

  /* Reveal elements that appear as the result of a click (e.g. trust badges). */
  function revealNow(elements) {
    if (reducedMotion || !flag('reveal')) return;
    elements.forEach(function (el) {
      el.classList.remove('is-visible');
      el.classList.add('reveal-pending');
    });
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        elements.forEach(function (el) {
          el.classList.add('is-visible');
        });
      });
    });
  }

  /* -------------------------------------------------------------- Parallax */
  var parallaxItems = [];
  var parallaxRaf = 0;
  function updateParallax() {
    parallaxRaf = 0;
    var vh = window.innerHeight;
    parallaxItems.forEach(function (item) {
      var rect = item.wrapper.getBoundingClientRect();
      if (rect.bottom < -vh || rect.top > vh * 2) return;
      // -1 (fully above) .. 1 (fully below); 0 when centered in viewport
      var progress = (rect.top + rect.height / 2 - vh / 2) / vh;
      item.inner.style.transform = 'translate3d(0, ' + (progress * -100 * item.speed).toFixed(2) + 'px, 0)';
    });
  }
  function requestParallax() {
    if (!parallaxRaf) parallaxRaf = requestAnimationFrame(updateParallax);
  }
  function initParallax(root) {
    if (reducedMotion) return;
    root.querySelectorAll('[data-parallax]').forEach(function (wrapper) {
      if (!once(wrapper, 'parallaxBound')) return;
      var inner = wrapper.querySelector('[data-parallax-inner]');
      var speed = parseFloat(wrapper.getAttribute('data-speed') || '0.15');
      if (!inner || !speed) return;
      parallaxItems.push({ wrapper: wrapper, inner: inner, speed: speed });
    });
    requestParallax();
  }
  window.addEventListener('scroll', requestParallax, { passive: true });
  window.addEventListener('resize', requestParallax);

  /* -------------------------------------------------------------- Magnetic */
  function initMagnetic(root) {
    if (reducedMotion || !finePointer || !flag('magnetic')) return;
    root.querySelectorAll('[data-magnetic]').forEach(function (el) {
      if (!once(el, 'magneticBound')) return;
      var strength = parseFloat(el.getAttribute('data-strength') || '0.35');
      el.addEventListener('mousemove', function (e) {
        var rect = el.getBoundingClientRect();
        var x = (e.clientX - rect.left - rect.width / 2) * strength;
        var y = (e.clientY - rect.top - rect.height / 2) * strength;
        el.style.transform = 'translate3d(' + x.toFixed(1) + 'px, ' + y.toFixed(1) + 'px, 0)';
      });
      el.addEventListener('mouseleave', function () {
        el.style.transform = '';
      });
    });
  }

  /* ------------------------------------------------------------------ Tilt */
  function initTilt(root) {
    if (reducedMotion || !finePointer || !flag('tilt')) return;
    root.querySelectorAll('[data-tilt]').forEach(function (el) {
      if (!once(el, 'tiltBound')) return;
      var glare = el.querySelector('[data-tilt-glare]');
      el.addEventListener('mousemove', function (e) {
        var rect = el.getBoundingClientRect();
        var px = (e.clientX - rect.left) / rect.width;
        var py = (e.clientY - rect.top) / rect.height;
        var rotateY = (px - 0.5) * 10;
        var rotateX = (0.5 - py) * 10;
        el.style.transform =
          'perspective(900px) rotateX(' + rotateX.toFixed(2) + 'deg) rotateY(' + rotateY.toFixed(2) + 'deg) translateY(-6px)';
        if (glare) {
          glare.style.background =
            'radial-gradient(circle at ' + px * 100 + '% ' + py * 100 + '%, rgba(255,255,255,0.14), transparent 55%)';
        }
      });
      el.addEventListener('mouseleave', function () {
        el.style.transform = '';
        if (glare) glare.style.background = '';
      });
    });
  }

  /* ------------------------------------------------------- Scroll progress */
  var progressBar = document.querySelector('[data-scroll-progress]');
  var progressRaf = 0;
  function updateProgress() {
    progressRaf = 0;
    var doc = document.documentElement;
    var max = doc.scrollHeight - doc.clientHeight;
    var pct = max > 0 ? (doc.scrollTop / max) * 100 : 0;
    progressBar.style.width = pct + '%';
  }
  if (progressBar) {
    var onProgress = function () {
      if (!progressRaf) progressRaf = requestAnimationFrame(updateProgress);
    };
    updateProgress();
    window.addEventListener('scroll', onProgress, { passive: true });
    window.addEventListener('resize', onProgress);
  }

  /* -------------------------------------------------------------- Age gate */
  function initAgeGate(root) {
    var gate = root.querySelector('[data-age-gate]');
    if (!gate || !once(gate, 'ageBound')) return;
    var key = gate.getAttribute('data-storage-key') || 'ivape-age-verified-v1';
    var inEditor = body.classList.contains('shopify-design-mode');
    var verified = false;
    try {
      verified = window.localStorage.getItem(key) === 'yes';
    } catch (e) {}
    var previousOverflow = '';
    var show = function () {
      gate.hidden = false;
      previousOverflow = body.style.overflow;
      body.style.overflow = 'hidden';
      var dialog = gate.querySelector('[data-age-dialog]');
      if (dialog) dialog.focus();
    };
    var hide = function () {
      gate.hidden = true;
      body.style.overflow = previousOverflow;
    };
    if (!verified && !inEditor) show();
    var yes = gate.querySelector('[data-age-yes]');
    var no = gate.querySelector('[data-age-no]');
    if (yes)
      yes.addEventListener('click', function () {
        try {
          window.localStorage.setItem(key, 'yes');
        } catch (e) {}
        hide();
      });
    if (no)
      no.addEventListener('click', function () {
        gate.querySelector('[data-age-prompt]').hidden = true;
        gate.querySelector('[data-age-blocked]').hidden = false;
      });
  }

  /* ----------------------------------------------------------- Mobile menu */
  function initMenu(root) {
    var toggle = root.querySelector('[data-menu-toggle]');
    var menu = root.querySelector('[data-mobile-menu]');
    if (!toggle || !menu || !once(toggle, 'menuBound')) return;
    var openIcon = toggle.querySelector('[data-menu-icon-open]');
    var closeIcon = toggle.querySelector('[data-menu-icon-close]');
    var original = '';
    var set = function (open) {
      menu.hidden = !open;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (openIcon) openIcon.hidden = open;
      if (closeIcon) closeIcon.hidden = !open;
      if (open) {
        original = body.style.overflow;
        body.style.overflow = 'hidden';
      } else {
        body.style.overflow = original;
      }
    };
    toggle.addEventListener('click', function () {
      set(menu.hidden);
    });
    menu.querySelectorAll('[data-menu-close], a').forEach(function (el) {
      el.addEventListener('click', function () {
        set(false);
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !menu.hidden) set(false);
    });
  }

  /* ------------------------------------------------ Disclosure (trust badges) */
  function initDisclosure(root) {
    root.querySelectorAll('[data-disclosure]').forEach(function (wrap) {
      if (!once(wrap, 'disclosureBound')) return;
      var btn = wrap.querySelector('[data-disclosure-toggle]');
      var panel = wrap.querySelector('[data-disclosure-panel]');
      var label = wrap.querySelector('[data-disclosure-label]');
      var arrow = wrap.querySelector('[data-disclosure-arrow]');
      if (!btn || !panel) return;
      btn.addEventListener('click', function () {
        var open = panel.hidden;
        panel.hidden = !open;
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (label) label.textContent = open ? btn.getAttribute('data-label-close') : btn.getAttribute('data-label-open');
        if (arrow) arrow.classList.toggle('rotate-180', open);
        if (open) revealNow(Array.prototype.slice.call(panel.querySelectorAll('[data-reveal-on-open]')));
      });
    });
  }

  /* --------------------------------------------------------- Shared helpers */
  var ACTIVE = ['border-accent', 'bg-accent', 'text-accent-fg'];
  var INACTIVE = ['border-border', 'text-muted', 'hover:border-border-strong', 'hover:text-fg'];
  function setActive(btn, active) {
    ACTIVE.forEach(function (c) {
      btn.classList.toggle(c, active);
    });
    INACTIVE.forEach(function (c) {
      btn.classList.toggle(c, !active);
    });
  }
  function replayPopIn(el, delayMs) {
    el.classList.remove('pop-in');
    void el.offsetWidth; // restart CSS animation
    el.style.animationDelay = delayMs + 'ms';
    el.classList.add('pop-in');
  }

  /* Sold-out items go last (stable). */
  function isSoldOut(el) {
    return el.getAttribute('data-available') === 'false';
  }
  function stockFirst(list) {
    return list
      .map(function (el, i) {
        return { el: el, i: i };
      })
      .sort(function (a, b) {
        return (isSoldOut(a.el) ? 1 : 0) - (isSoldOut(b.el) ? 1 : 0) || a.i - b.i;
      })
      .map(function (e) {
        return e.el;
      });
  }

  /* ------------------------------------------------ Home brand tabs + pop-in */
  function initProductTabs(root) {
    root.querySelectorAll('[data-product-tabs]').forEach(function (wrap) {
      if (!once(wrap, 'tabsBound')) return;
      var limit = parseInt(wrap.getAttribute('data-limit') || '4', 10);
      var tabs = wrap.querySelectorAll('[data-tab]');
      var items = stockFirst(Array.prototype.slice.call(wrap.querySelectorAll('[data-tab-item]')));
      items.forEach(function (item) {
        item.parentNode.appendChild(item);
      });
      var empty = wrap.querySelector('[data-tab-empty]');
      function show(id, animate) {
        var shown = 0;
        items.forEach(function (item) {
          var match = id === 'all' || item.getAttribute('data-brand') === id;
          var visible = match && shown < limit;
          item.hidden = !visible;
          if (visible) {
            if (animate) replayPopIn(item, shown * 70);
            shown++;
          }
        });
        if (empty) empty.hidden = shown > 0;
      }
      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var id = tab.getAttribute('data-tab');
          tabs.forEach(function (t) {
            var on = t === tab;
            t.setAttribute('aria-selected', on ? 'true' : 'false');
            setActive(t, on);
          });
          show(id, true);
        });
      });
      var current = wrap.querySelector('[data-tab][aria-selected="true"]');
      show(current ? current.getAttribute('data-tab') : 'all', false);
    });
  }

  /* ------------------------------------------- Collection filters (URL state) */
  function initCollectionFilters(root) {
    root.querySelectorAll('[data-collection-filters]').forEach(function (wrap) {
      if (!once(wrap, 'filtersBound')) return;
      var grid = wrap.querySelector('[data-filter-grid]');
      var items = grid ? Array.prototype.slice.call(grid.querySelectorAll('[data-filter-item]')) : [];
      var empty = wrap.querySelector('[data-filter-empty]');
      var queryRow = wrap.querySelector('[data-query-row]');
      var queryText = wrap.querySelector('[data-query-text]');
      var params = new URLSearchParams(window.location.search);
      var state = {
        brand: params.get('brand') || '',
        tag: params.get('tag') || '',
        minPuffs: params.get('minPuffs') || '',
        sort: params.get('sort') || '',
        q: (params.get('q') || '').toLowerCase()
      };

      function apply(animate) {
        wrap.querySelectorAll('[data-filter-key]').forEach(function (btn) {
          var key = btn.getAttribute('data-filter-key');
          if (key === 'q') return;
          var on = (state[key] || '') === btn.getAttribute('data-filter-value');
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
          setActive(btn, on);
        });
        if (queryRow) {
          queryRow.hidden = !state.q;
          if (queryText) queryText.textContent = state.q;
        }
        var visible = items.filter(function (item) {
          var card = item.querySelector('[data-product-card]');
          if (!card) return true;
          if (state.brand && card.getAttribute('data-brand') !== state.brand) return false;
          if (state.tag && (card.getAttribute('data-tags') || '').split(',').indexOf(state.tag) === -1) return false;
          var puffs = parseInt(card.getAttribute('data-puffs') || '0', 10);
          if (state.minPuffs && (!puffs || puffs < parseInt(state.minPuffs, 10))) return false;
          if (state.q && (card.getAttribute('data-search') || '').indexOf(state.q) === -1) return false;
          return true;
        });
        var sorted = visible.slice();
        var puffsOf = function (i) {
          var c = i.querySelector('[data-product-card]');
          var v = parseInt((c && c.getAttribute('data-puffs')) || '', 10);
          return isNaN(v) ? null : v;
        };
        if (state.sort === 'puffs-desc' || state.sort === 'puffs-asc') {
          sorted.sort(function (a, b) {
            // products without a puff count always go last (same as source)
            var pa = puffsOf(a);
            var pb = puffsOf(b);
            if (pa == null && pb == null) return 0;
            if (pa == null) return 1;
            if (pb == null) return -1;
            return state.sort === 'puffs-desc' ? pb - pa : pa - pb;
          });
        } else if (state.sort === 'price-asc' || state.sort === 'price-desc') {
          sorted.sort(function (a, b) {
            var d = parseInt(a.getAttribute('data-price'), 10) - parseInt(b.getAttribute('data-price'), 10);
            return state.sort === 'price-asc' ? d : -d;
          });
        } else {
          sorted.sort(function (a, b) {
            return parseInt(a.getAttribute('data-index'), 10) - parseInt(b.getAttribute('data-index'), 10);
          });
        }
        sorted = stockFirst(sorted);
        items.forEach(function (item) {
          item.hidden = sorted.indexOf(item) === -1;
        });
        sorted.forEach(function (item, i) {
          grid.appendChild(item);
          if (animate) replayPopIn(item, (i % 8) * 70);
        });
        if (empty) empty.hidden = sorted.length > 0;
      }

      function pushUrl() {
        var p = new URLSearchParams(window.location.search);
        ['brand', 'tag', 'minPuffs', 'sort', 'q'].forEach(function (k) {
          if (state[k]) p.set(k, state[k]);
          else p.delete(k);
        });
        var qs = p.toString();
        history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : ''));
      }

      wrap.querySelectorAll('[data-filter-key]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          state[btn.getAttribute('data-filter-key')] = btn.getAttribute('data-filter-value');
          pushUrl();
          apply(true);
        });
      });
      apply(false);
    });
  }

  /* ---------------------------------------------------------- Flavor guide */
  function initGuide(root) {
    root.querySelectorAll('[data-flavor-guide]').forEach(function (wrap) {
      if (!once(wrap, 'guideBound')) return;
      var buttons = Array.prototype.slice.call(wrap.querySelectorAll('[data-fruit]'));
      var ring = buttons.map(function (b) {
        return b.getAttribute('data-fruit');
      });
      var keywords = {};
      buttons.forEach(function (b) {
        keywords[b.getAttribute('data-fruit')] = (b.getAttribute('data-keywords') || '')
          .split(',')
          .map(function (s) {
            return s.trim();
          })
          .filter(Boolean);
      });
      var pool = stockFirst(Array.prototype.slice.call(wrap.querySelectorAll('[data-guide-item]')));
      var fruitsOf = function (item) {
        var card = item.querySelector('[data-product-card]');
        var tagged = ((card && card.getAttribute('data-fruits')) || '').split(/\s+/).filter(Boolean);
        if (tagged.length) return tagged;
        // No fruit-* tag: infer from the product name.
        var text = (card && card.getAttribute('data-search')) || '';
        return ring.filter(function (f) {
          return keywords[f].some(function (k) {
            return text.indexOf(k) !== -1;
          });
        });
      };
      var index = pool.map(function (item) {
        return { item: item, fruits: fruitsOf(item) };
      });
      var result = wrap.querySelector('[data-guide-result]');
      var exactGrid = wrap.querySelector('[data-guide-exact]');
      var closeGrid = wrap.querySelector('[data-guide-close]');

      function select(fruit) {
        var btn = buttons.filter(function (b) {
          return b.getAttribute('data-fruit') === fruit;
        })[0];
        if (!btn) return;
        buttons.forEach(function (b) {
          var on = b === btn;
          b.setAttribute('aria-checked', on ? 'true' : 'false');
          b.classList.toggle('border-accent', on);
          b.classList.toggle('bg-accent/10', on);
          b.classList.toggle('border-border', !on);
          b.classList.toggle('bg-bg-elevated', !on);
        });
        var i = ring.indexOf(fruit);
        var neighbors = [ring[(i - 1 + ring.length) % ring.length], ring[(i + 1) % ring.length]];
        var exact = index.filter(function (e) {
          return e.fruits.indexOf(fruit) !== -1;
        });
        var close = index.filter(function (e) {
          return (
            e.fruits.indexOf(fruit) === -1 &&
            e.fruits.some(function (f) {
              return neighbors.indexOf(f) !== -1;
            })
          );
        });
        var fill = function (grid, list) {
          grid.innerHTML = '';
          list.slice(0, 12).forEach(function (e, n) {
            var clone = e.item.cloneNode(true);
            // clones carry the "*-bound" guards of the hidden pool; clear them so events re-bind
            [clone].concat(Array.prototype.slice.call(clone.querySelectorAll('*'))).forEach(function (n) {
              Object.keys(n.dataset || {}).forEach(function (k) {
                if (/Bound$/.test(k)) delete n.dataset[k];
              });
            });
            clone.style.animationDelay = n * 70 + 'ms';
            grid.appendChild(clone);
          });
        };
        fill(exactGrid, exact);
        fill(closeGrid, close);
        wrap.querySelectorAll('[data-guide-fruit]').forEach(function (el) {
          el.textContent = btn.getAttribute('data-fruit-label');
        });
        wrap.querySelector('[data-guide-exact-wrap]').hidden = exact.length === 0;
        wrap.querySelector('[data-guide-no-exact]').hidden = exact.length > 0;
        wrap.querySelector('[data-guide-close-wrap]').hidden = close.length === 0;
        wrap.querySelector('[data-guide-none]').hidden = exact.length + close.length > 0;
        result.hidden = false;
        initTilt(result);
        initCartForms(result);
      }
      buttons.forEach(function (b) {
        b.addEventListener('click', function () {
          select(b.getAttribute('data-fruit'));
          var p = new URLSearchParams(window.location.search);
          p.set('fruit', b.getAttribute('data-fruit'));
          history.replaceState(null, '', window.location.pathname + '?' + p.toString());
        });
      });
      var initial = new URLSearchParams(window.location.search).get('fruit');
      if (initial) select(initial);
    });
  }

  /* ------------------------------------------------------------------ Cart */
  function formatMoney(cents) {
    var fmt = (window.theme && window.theme.moneyFormat) || '¥{{amount_no_decimals}}';
    var value = cents / 100;
    var withSep = function (n, d) {
      var parts = n.toFixed(d).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      if (key === 'amount_no_decimals') return withSep(value, 0);
      if (key === 'amount_with_comma_separator') return withSep(value, 2).replace('.', ',');
      return withSep(value, 2);
    });
  }

  function refreshCartCount() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
      });
  }

  function initCartForms(root) {
    root.querySelectorAll('[data-ajax-cart-form]').forEach(function (form) {
      if (!once(form, 'cartBound')) return;
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var btn = form.querySelector('[data-add-button]');
        var label = btn ? btn.innerHTML : '';
        if (btn) btn.disabled = true;
        fetch(window.theme.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form)
        })
          .then(function (r) {
            return r.json().then(function (json) {
              if (!r.ok) throw json;
              return json;
            });
          })
          .then(function () {
            refreshCartCount();
            if (btn) {
              // "追加しました ✓" state for 1.6s, as in add-to-cart-button.tsx
              btn.textContent = window.theme.strings.added;
              btn.classList.remove('bg-accent', 'text-accent-fg');
              btn.classList.add('bg-fg', 'text-bg');
              setTimeout(function () {
                btn.innerHTML = label;
                btn.classList.add('bg-accent', 'text-accent-fg');
                btn.classList.remove('bg-fg', 'text-bg');
                btn.disabled = false;
              }, 1600);
            }
          })
          .catch(function (err) {
            if (btn) {
              btn.textContent = (err && err.description) || window.theme.strings.error;
              setTimeout(function () {
                btn.innerHTML = label;
                btn.disabled = false;
              }, 2200);
            }
          });
      });
    });
  }

  function initCartPage(root) {
    var section = root.querySelector('[data-cart-section]');
    if (!section || !once(section, 'cartPageBound')) return;
    section.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-cart-qty]');
      if (!btn) return;
      e.preventDefault();
      section.style.opacity = '0.6';
      var sectionId = section.getAttribute('data-section-id');
      fetch(window.theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: parseInt(btn.getAttribute('data-line-index'), 10),
          quantity: Math.max(0, parseInt(btn.getAttribute('data-cart-qty'), 10)),
          sections: [sectionId],
          sections_url: window.location.pathname
        })
      })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          var html = cart.sections && cart.sections[sectionId];
          if (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var fresh = doc.querySelector('[data-cart-section]');
            if (fresh) {
              section.replaceWith(fresh);
              initCartPage(document);
            }
          } else {
            window.location.reload();
          }
          document.querySelectorAll('[data-cart-count]').forEach(function (el) {
            el.textContent = cart.item_count;
            el.hidden = cart.item_count === 0;
          });
        })
        .catch(function () {
          window.location.reload();
        });
    });
  }

  /* --------------------------------------------------------- Product page */
  function initProduct(root) {
    root.querySelectorAll('[data-product-section]').forEach(function (wrap) {
      if (!once(wrap, 'productBound')) return;
      var jsonEl = wrap.querySelector('[data-product-json]');
      var product = jsonEl ? JSON.parse(jsonEl.textContent) : null;
      var form = wrap.querySelector('[data-product-form]');
      var idInput = wrap.querySelector('[data-variant-id]');
      var priceEl = wrap.querySelector('[data-price]');
      var compareEl = wrap.querySelector('[data-compare-price]');
      var addBtn = wrap.querySelector('[data-add-button]');
      var mainImg = wrap.querySelector('[data-main-media] img');
      var qty = wrap.querySelector('[data-qty-input]');

      // quantity stepper
      var minus = wrap.querySelector('[data-qty-minus]');
      var plus = wrap.querySelector('[data-qty-plus]');
      if (qty && minus && plus) {
        minus.addEventListener('click', function () {
          qty.value = Math.max(1, (parseInt(qty.value, 10) || 1) - 1);
        });
        plus.addEventListener('click', function () {
          qty.value = (parseInt(qty.value, 10) || 1) + 1;
        });
      }

      // thumbnails
      wrap.querySelectorAll('[data-thumb]').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          if (mainImg) {
            mainImg.removeAttribute('srcset');
            mainImg.src = thumb.getAttribute('data-thumb');
          }
          wrap.querySelectorAll('[data-thumb]').forEach(function (t) {
            t.classList.toggle('border-accent', t === thumb);
            t.classList.toggle('border-border', t !== thumb);
          });
        });
      });

      // variant picker (option pills)
      if (!product || !form) return;
      var fieldsets = Array.prototype.slice.call(wrap.querySelectorAll('[data-option-index]'));
      if (!fieldsets.length) return;
      fieldsets.forEach(function (fs) {
        fs.querySelectorAll('[data-option-value]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            fs.querySelectorAll('[data-option-value]').forEach(function (b) {
              var on = b === btn;
              b.setAttribute('aria-pressed', on ? 'true' : 'false');
              setActive(b, on);
            });
            var selected = fieldsets.map(function (f) {
              var active = f.querySelector('[aria-pressed="true"]');
              return active ? active.getAttribute('data-option-value') : null;
            });
            var variant = product.variants.filter(function (v) {
              return v.options.every(function (o, i) {
                return o === selected[i];
              });
            })[0];
            if (!variant) return;
            idInput.value = variant.id;
            if (priceEl) priceEl.textContent = formatMoney(variant.price);
            if (compareEl) {
              compareEl.hidden = !(variant.compare_at_price > variant.price);
              if (!compareEl.hidden) compareEl.textContent = formatMoney(variant.compare_at_price);
            }
            if (addBtn) {
              addBtn.disabled = !variant.available;
              addBtn.textContent = variant.available ? window.theme.strings.addToCart : window.theme.strings.soldOut;
            }
            if (variant.featured_image && mainImg) {
              mainImg.removeAttribute('srcset');
              mainImg.src = variant.featured_image.src;
            }
            var url = new URL(window.location.href);
            url.searchParams.set('variant', variant.id);
            history.replaceState(null, '', url.toString());
          });
        });
      });
    });
  }

  /* ------------------------------------------------------ Recommendations */
  function initRecommendations(root) {
    root.querySelectorAll('[data-recommendations]').forEach(function (el) {
      if (!once(el, 'recoBound') || el.children.length) return;
      fetch(el.getAttribute('data-url'))
        .then(function (r) {
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-recommendations]');
          if (fresh && fresh.innerHTML.trim()) {
            el.innerHTML = fresh.innerHTML;
            initAll(el);
          }
        })
        .catch(function () {});
    });
  }

  /* ------------------------------------------------------------------ Boot */
  function initAll(root) {
    initReveal(root);
    initParallax(root);
    initMagnetic(root);
    initTilt(root);
    initAgeGate(root);
    initMenu(root);
    initDisclosure(root);
    initProductTabs(root);
    initCollectionFilters(root);
    initGuide(root);
    initCartForms(root);
    initCartPage(root);
    initProduct(root);
    initRecommendations(root);
  }

  initAll(document);
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
